import React, { type MouseEvent } from "react";
import classNames from "classnames";
import type { ActionButtonProps } from "./types";
import { Icon } from "@/components/ui/display/Icon";
import styles from "./ActionButton.module.css";

/**
 * ActionButton component - A styled button with semantic variants, icons, and hover effects.
 * Ported from Vue ActionButton.vue component.
 *
 * @example
 * ```tsx
 * <ActionButton variant="primary" label="Save" />
 * <ActionButton variant="danger" label="Delete" />
 * <ActionButton variant="secondary" label="Cancel" />
 * ```
 */
export const ActionButton = React.forwardRef<
  HTMLButtonElement,
  ActionButtonProps
>(
  (
    {
      label = "Button",
      icon,
      hoverIcon,
      rotateIcon = false,
      iconPosition = "right",
      selected = false,
      variant = "primary",
      hoverVariant,
      rounded = false,
      disabled = false,
      size = "medium",
      onClick,
      "data-testid": testId,
    },
    ref,
  ) => {
    const effectiveHoverVariant = hoverVariant ?? variant;
    const hasHoverIcon = hoverIcon && hoverIcon !== icon;

    const renderIcon = (iconNode: React.ReactNode) => {
      if (typeof iconNode === "string") {
        return <Icon name={iconNode} size="sm" aria-hidden />;
      }
      return iconNode;
    };

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      if (!disabled && onClick) {
        onClick(event);
      }
    };

    return (
      <button
        ref={ref}
        type="button"
        className={classNames(styles.button, styles[variant], styles[size], {
          [styles.selected]: selected,
          [styles.disabled]: disabled,
          [styles.rounded]: rounded,
          [styles.rotateIcon]: rotateIcon,
          [styles.reverseOrder]: iconPosition === "left",
          [styles[`hover-${effectiveHoverVariant}`]]: true,
        })}
        onClick={handleClick}
        disabled={disabled}
        data-testid={testId}
      >
        <span className={styles.label}>{label}</span>

        {icon && (
          <>
            {hasHoverIcon ? (
              <>
                <span className={styles.iconDefault}>{renderIcon(icon)}</span>
                <span className={styles.iconHover}>
                  {hoverIcon ? renderIcon(hoverIcon) : null}
                </span>
              </>
            ) : (
              <span className={styles.icon}>{renderIcon(icon)}</span>
            )}
          </>
        )}
      </button>
    );
  },
);

ActionButton.displayName = "ActionButton";

export default ActionButton;
